import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronRight, CalendarDays, Search, X, Plus, Check,
  UserRound, MapPin, Phone, ClipboardList, Loader2, ShieldAlert,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/api/apiClient';
import { useOdooAuth } from '@/lib/OdooAuthContext';

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function PlanVisits() {
  const navigate = useNavigate();
  const { odooSession } = useOdooAuth();
  const role = odooSession?.role;
  const canPlan = role && role !== 'rep';

  const [reps, setReps] = useState([]);
  const [repsLoading, setRepsLoading] = useState(true);
  const [rep, setRep] = useState(null);
  const [date, setDate] = useState(todayStr());
  const [notes, setNotes] = useState('');

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const searchTimer = useRef(null);

  // ── Load the team's reps ───────────────────────────────────────────
  useEffect(() => {
    if (!canPlan) { setRepsLoading(false); return; }
    api.get('/sellgo_team_reps')
      .then(r => { if (r?.success) setReps(r.data || []); })
      .catch(() => {})
      .finally(() => setRepsLoading(false));
  }, [canPlan]);

  // ── Customer search ────────────────────────────────────────────────
  const runSearch = useCallback(async (q) => {
    setSearching(true);
    const params = { search: q || undefined, limit: 20, salesman_uid: rep?.uid };
    let res;
    try {
      res = await api.get('/sellgo_v5_customer_search', params);
    } catch {
      res = await api.get('/customers', params);
    }
    if (res?.success) setResults(res.data || []);
    setSearching(false);
  }, [rep]);

  const handleSearch = (v) => {
    setQuery(v);
    clearTimeout(searchTimer.current);
    if (!v.trim()) { setResults([]); return; }
    searchTimer.current = setTimeout(() => runSearch(v), 350);
  };

  const addCustomer = (c) => {
    setSelected(prev => prev.find(x => x.id === c.id) ? prev : [...prev, c]);
    // Collapse the search like the gifts screen — search again to add more.
    setQuery('');
    setResults([]);
  };
  const removeCustomer = (id) => setSelected(prev => prev.filter(c => c.id !== id));

  const save = async () => {
    if (!rep) { toast.error('اختر المندوب أولاً'); return; }
    if (!date) { toast.error('اختر تاريخ الخطة'); return; }
    if (selected.length === 0) { toast.error('أضف عميل واحد على الأقل'); return; }
    setSaving(true);
    try {
      const res = await api.post('/visit-plans', {
        user_id: rep.uid,
        plan_date: date,
        plan_type: 'daily',
        customer_ids: selected.map(c => c.id),
        notes,
      });
      if (res?.success) {
        toast.success(`تم تجهيز خطة بـ ${selected.length} زيارة للمندوب ${rep.name}`);
        navigate('/visits');
      } else {
        toast.error(res?.error || 'فشل حفظ الخطة');
      }
    } catch (e) {
      toast.error(e?.message || 'فشل حفظ الخطة');
    }
    setSaving(false);
  };

  // ── Not authorised (plain rep) ─────────────────────────────────────
  if (!canPlan) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh] p-6 gap-3 text-center" dir="rtl">
        <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center">
          <ShieldAlert className="w-8 h-8 text-muted-foreground" />
        </div>
        <h2 className="font-bold text-foreground text-lg">غير متاح</h2>
        <p className="text-sm text-muted-foreground">تخطيط زيارات المناديب متاح للمشرف والمدير فقط.</p>
        <Button onClick={() => navigate('/visits')} className="bg-primary rounded-xl h-11 mt-2">العودة للزيارات</Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" dir="rtl">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 bg-card border-b border-border sticky top-0 z-10">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate('/visits')} className="p-1.5 rounded-xl hover:bg-muted transition-colors">
            <ChevronRight className="w-5 h-5 text-foreground" />
          </button>
          <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
            <ClipboardList className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="font-bold text-foreground leading-tight">تخطيط زيارات المناديب</h1>
            <p className="text-xs text-muted-foreground">جهّز خطة زيارات بعملاء وتاريخ، والمندوب ينفّذها</p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 pb-40 space-y-5">
        {/* 1) Rep */}
        <div>
          <p className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5">
            <UserRound className="w-4 h-4 text-primary" /> المندوب
          </p>
          {repsLoading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : reps.length === 0 ? (
            <p className="text-sm text-muted-foreground bg-card border border-border rounded-2xl p-4 text-center">لا يوجد مناديب في فريقك.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {reps.map(r => {
                const active = rep?.uid === r.uid;
                return (
                  <button key={r.uid} onClick={() => setRep(r)}
                    className={`text-right rounded-2xl p-3 border transition-all flex items-center gap-2 ${
                      active ? 'border-primary bg-primary/5 shadow-sm' : 'border-border bg-card hover:border-primary/40'
                    }`}>
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${active ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`}>
                      {active ? <Check className="w-4 h-4" /> : <span className="font-bold">{r.name?.charAt(0) || '؟'}</span>}
                    </div>
                    <span className="text-sm font-semibold text-foreground truncate">{r.name}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* 2) Date */}
        <div>
          <p className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5">
            <CalendarDays className="w-4 h-4 text-primary" /> تاريخ الخطة
          </p>
          <Input type="date" value={date} min={todayStr()} onChange={e => setDate(e.target.value)}
            className="rounded-xl bg-card h-11" />
        </div>

        {/* 3) Customers */}
        <div>
          <p className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5">
            <MapPin className="w-4 h-4 text-primary" /> العملاء ({selected.length})
          </p>

          {/* Selected chips */}
          {selected.length > 0 && (
            <div className="space-y-2 mb-3">
              {selected.map((c, i) => (
                <div key={c.id} className="bg-card border border-primary/40 rounded-2xl p-3 flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-foreground truncate">{c.name}</p>
                    {(c.phone || c.mobile || c.city) && (
                      <p className="text-xs text-muted-foreground truncate">{[c.phone || c.mobile, c.city].filter(Boolean).join(' • ')}</p>
                    )}
                  </div>
                  <button onClick={() => removeCustomer(c.id)} className="p-1.5 text-muted-foreground hover:text-red-500 transition-colors flex-shrink-0">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Search */}
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={query} onChange={e => handleSearch(e.target.value)}
              placeholder="ابحث عن عميل بالاسم أو الهاتف..." className="pr-10 rounded-xl bg-card" />
            {query && (
              <button onClick={() => handleSearch('')} className="absolute left-3 top-1/2 -translate-y-1/2">
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            )}
          </div>

          {searching ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
          ) : results.length > 0 ? (
            <div className="mt-2 space-y-1.5">
              {results.map(c => {
                const added = selected.find(x => x.id === c.id);
                return (
                  <button key={c.id} onClick={() => addCustomer(c)} disabled={!!added}
                    className={`w-full text-right flex items-center gap-3 p-2.5 rounded-xl border transition-all ${
                      added ? 'border-primary/30 bg-primary/5 opacity-70' : 'border-border bg-card hover:border-primary/40'
                    }`}>
                    <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-primary font-bold">{c.name?.charAt(0) || '؟'}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{c.name}</p>
                      {(c.phone || c.mobile) && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1 truncate"><Phone className="w-3 h-3" />{c.phone || c.mobile}</p>
                      )}
                    </div>
                    {added
                      ? <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      : <Plus className="w-4 h-4 text-primary flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          ) : query ? (
            <p className="text-center text-sm text-muted-foreground py-4">لا يوجد عملاء مطابقون</p>
          ) : null}
        </div>

        {/* Notes */}
        <div>
          <p className="text-sm font-semibold text-foreground mb-2">ملاحظات (اختياري)</p>
          <Textarea value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="تعليمات للمندوب..." rows={2} className="rounded-xl" />
        </div>
      </div>

      {/* Sticky footer */}
      <div className="fixed bottom-0 right-0 left-0 bg-card border-t border-border z-30 px-4 pt-3 pb-4 space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            {rep ? rep.name : 'لم تختر مندوب'} • {selected.length} زيارة
          </span>
          <span className="text-muted-foreground">{date}</span>
        </div>
        <Button onClick={save} disabled={saving || !rep || !date || selected.length === 0}
          className="w-full bg-primary h-12 text-base font-semibold rounded-xl gap-2">
          {saving ? <><Loader2 className="w-5 h-5 animate-spin" /> جاري الحفظ...</> : <><Check className="w-5 h-5" /> حفظ خطة الزيارات</>}
        </Button>
      </div>
    </div>
  );
}
